import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import { dispositionFileName, printLogQuery } from './presentation';
import {
  printEventsPageSchema,
  printLogPeopleSchema,
  settingsResponseSchema,
  type PrintEvent,
  type PrintLogFilters,
  type PrintLogPerson,
  type Purpose,
  type WatermarkSettings,
} from './types';

// Consultas de Marca de agua e impresión (so=400). La empresa activa va en la posición 1
// del queryKey: nunca se sirve la caché de otra empresa.

interface Scope {
  companyId: string | null;
  enabled: boolean;
}

export interface PrintLogPage {
  items: PrintEvent[];
  nextCursor: string | null;
}

export function useWatermarkSettings({
  companyId,
  enabled,
}: Scope): UseQueryResult<WatermarkSettings> {
  const api = useApi();
  return useQuery({
    queryKey: ['watermark-settings', companyId],
    queryFn: async () =>
      settingsResponseSchema.parse(await api.get<unknown>('/api/v1/quality/watermark-settings'))
        .data,
    enabled,
  });
}

export function useUpdateWatermarkSettings(): UseMutationResult<unknown, Error, WatermarkSettings> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put<unknown>('/api/v1/quality/watermark-settings', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watermark-settings'] }),
  });
}

export function usePrintLog(
  filters: PrintLogFilters,
  { companyId, enabled }: Scope,
): UseInfiniteQueryResult<InfiniteData<PrintLogPage, string | null>> {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['print-log', companyId, filters],
    queryFn: async ({ pageParam }) => {
      const raw = await api.get<unknown>(
        `/api/v1/quality/print-events?${printLogQuery(filters, pageParam)}`,
      );
      const parsed = printEventsPageSchema.parse(raw);
      return { items: parsed.data, nextCursor: parsed.meta.next_cursor };
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

/** Personas del registro que coinciden con `q` (G1-3); consulta con 2 letras o más. */
export function usePrintLogPeople(
  q: string,
  { companyId, enabled }: Scope,
): UseQueryResult<PrintLogPerson[]> {
  const api = useApi();
  return useQuery({
    queryKey: ['print-log-people', companyId, q],
    queryFn: async () =>
      printLogPeopleSchema.parse(
        await api.get<unknown>(`/api/v1/quality/print-events/people?q=${encodeURIComponent(q)}`),
      ).data,
    enabled: enabled && q.length >= 2,
    staleTime: 30_000,
  });
}

export interface CopyRequest {
  entryId: string;
  purpose: Purpose;
  mode: 'print_view' | 'download';
}

/** Genera una copia marcada (el servidor registra el evento) y devuelve PDF y folio. */
export function usePrintCopy(): UseMutationResult<
  { blob: Blob; folio: string | null; filename: string | null },
  Error,
  CopyRequest
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entryId, purpose, mode }) => {
      const res = await api.getBlob(
        `/api/v1/quality/documents/${entryId}/watermarked-pdf?purpose=${purpose}&mode=${mode}`,
      );
      return {
        blob: res.blob,
        folio: res.headers.get('x-print-folio'),
        filename: dispositionFileName(res.headers.get('content-disposition')),
      };
    },
    // Una copia nueva es un evento nuevo en la bitácora.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['print-log'] }),
  });
}
